import * as React from 'react';

import type {SalesAutomationStageset as SalesAutomationStagesetFragment} from 'nutshell-graphql-types';

import {
    shouldShowMailchimp,
    shouldShowConstantContact,
} from '../../../integrations/should-show-buttons';

import {groupIntegrationsByActiveStatus} from '../constants';
import type {AvailableIntegrations} from '../types';

import {SalesAutomationConstantContact} from './board/sales-automation-constant-contact';
import {SalesAutomationDripSequenceTriggers} from './board/sales-automation-drip-sequence-triggers';
import {SalesAutomationEmails} from './board/sales-automation-emails';
import {SalesAutomationGoals} from './board/sales-automation-goals';
import {SalesAutomationNutshellEmailMarketing} from './board/sales-automation-nutshell-email-marketing';
import {SalesAutomationSlack} from './board/sales-automation-slack';
import {SalesAutomationStepTemplates} from './board/sales-automation-step-templates';
import {SalesAutomationTextAutomations} from './board/sales-automation-text-automations';
import {SalesAutomationUserAssignments} from './board/sales-automation-user-assignments';

type Props = {
    isStageMoving: boolean;
    availableIntegrations: AvailableIntegrations;
    stageset: SalesAutomationStagesetFragment;
    onRefetchStageset: () => any;
};

/*
 * Renders the stages board within the SalesAutomationAppShell, which
 * provides the navigation and editing sidebars.
 */
export class SalesAutomationOpenLeadsBoard extends React.PureComponent<Props> {
    render() {
        const integrationConfig = {
            constantContactEnabled:
                this.props.availableIntegrations.hasConstantContactIntegration || false,
            mailchimpEnabled: this.props.availableIntegrations.hasMailChimpIntegration || false,
            quickbooksEnabled: NutClientConfig.integrations.quickbooksEnabled,
        } as const;
        const hasEmailMarketingEnabled =
            integrationConfig.constantContactEnabled || integrationConfig.mailchimpEnabled;

        // @ts-expect-error
        let integrationsList = [];

        if (NutClientConfig.hasEmailSequencesSalesAutomation) {
            // @ts-expect-error
            integrationsList = integrationsList.concat([
                {
                    isActive: true,
                    display: true,
                    component: (
                        <SalesAutomationNutshellEmailMarketing
                            key='NutshellEmailMarketing'
                            stages={this.props.stageset.stages}
                            hideCards={this.props.isStageMoving}
                        />
                    ),
                },
            ]);
        }

        if (hasEmailMarketingEnabled) {
            // If they have either email marketing integration enabled, just show the ones they have enabled
            if (this.props.availableIntegrations.hasMailChimpIntegration) {
                integrationsList = integrationsList.concat([
                    {
                        isActive: true,
                        display: shouldShowMailchimp(integrationConfig),
                        component: (
                            <SalesAutomationEmails
                                key='MailChimp'
                                stages={this.props.stageset.stages}
                                hideCards={this.props.isStageMoving}
                            />
                        ),
                    },
                ]);
            }
            if (this.props.availableIntegrations.hasConstantContactIntegration) {
                integrationsList = integrationsList.concat([
                    {
                        isActive: true,
                        display: shouldShowConstantContact(integrationConfig),
                        component: (
                            <SalesAutomationConstantContact
                                key='ConstantContact'
                                stages={this.props.stageset.stages}
                                hideCards={this.props.isStageMoving}
                                onConnectConstantContact={this.props.onRefetchStageset}
                            />
                        ),
                    },
                ]);
            }
        }

        integrationsList = integrationsList.concat([
            {
                isActive: this.props.availableIntegrations.hasSlackIntegration || false,
                display: NutClientConfig.canSlackAutomation,
                component: (
                    <SalesAutomationSlack
                        key='Slack'
                        stages={this.props.stageset.stages}
                        hasSlackIntegration={this.props.availableIntegrations.hasSlackIntegration}
                        hideCards={this.props.isStageMoving}
                    />
                ),
            },
        ]);

        return (
            <div>
                <SalesAutomationUserAssignments
                    stages={this.props.stageset.stages}
                    hideCards={this.props.isStageMoving}
                />
                <SalesAutomationGoals
                    stages={this.props.stageset.stages}
                    hideCards={this.props.isStageMoving}
                />
                <SalesAutomationStepTemplates
                    stages={this.props.stageset.stages}
                    hideCards={this.props.isStageMoving}
                />
                <SalesAutomationDripSequenceTriggers
                    stages={this.props.stageset.stages}
                    hideCards={this.props.isStageMoving}
                />
                <SalesAutomationTextAutomations
                    stages={this.props.stageset.stages}
                    hideCards={this.props.isStageMoving}
                />
                {groupIntegrationsByActiveStatus(integrationsList)}
            </div>
        );
    }
}
